/**
 * ImpersonationBannerSlot — shell-level mount of the impersonation banner.
 *
 * Mounted inside `WebAppShell` right after `<PreviewBanner />`, so the two
 * top-of-page warning bands stack vertically (preview above, impersonation
 * below) and travel with every authenticated route — not just `/dashboard`.
 *
 * Pre-2026-06-12: the impersonation note rendered only on `/dashboard`. The
 * banner now follows the same lifecycle as `<PreviewBanner />` — always
 * mounted, renders `null` unless the active identity is impersonating.
 *
 * Detection mirrors the rule that used to live in `Dashboard`:
 *   thrive_id === JARED_THRIVE_ID && email !== JARED_EMAIL
 *
 * The body copy depends on which of the TWO engineer row shapes the
 * identity has (SOP 10, rule of 2026-10-20):
 *   - rows created BEFORE 2026-10-20 also carry Jared's `hubspot_id`
 *     (`78133807`), so Thrive AND HubSpot calls hit Jared's records;
 *   - rows created SINCE carry the engineer's OWN `hubspot_id` +
 *     `hubspot_email`, so only Thrive is impersonated — HubSpot calls
 *     (sequence ownership, enroller, sender inbox) act as the engineer.
 * Saying "Thrive + HubSpot" unconditionally is wrong for the second
 * shape, which is why this branches instead of reading one sentence.
 *
 * THREE states, not two. `UserMe.hubspot_id` is nullable, and a row with
 * NO HubSpot user is neither shape — claiming "HubSpot calls act as you"
 * there asserts an identity that does not exist. The two-way branch failed
 * OPEN on exactly that state, which is the state SOP 07 used to instruct
 * operators to create (found by `/pr-review` #1086 iter 1).
 *
 * How reachable that is, stated as evidence rather than settled fact:
 * `app/auth/service.py` rejects a falsy `hubspot_id` with
 * `AccountNotFullyProvisioned` before a session is minted, so a shell
 * should never see one. But `UserMe.hubspot_id` is typed nullable, and
 * `SearchPagePanel.tsx:151` ships "slice 01 provisioning resilience" —
 * a disabled CTA and a handler guard, pinned by `SearchPagePanel.test.tsx`
 * — built on the premise that the state DOES occur. One of the two is
 * wrong. The cheap resolution is to keep the guard and not assert which;
 * the open question (is View 1b dead code, or is the gate wrong?) is
 * recorded as a follow-up in the PRD rather than settled here.
 *
 * `hubspot_id` is TRIMMED before comparison. A padded `" 78133807 "` would
 * otherwise miss `JARED_HUBSPOT_ID` and fall to the own-id branch —
 * claiming HubSpot acts as the engineer for a row that *is* Jared's HubSpot
 * user, the dangerous direction. Nothing upstream prevents the padding:
 * the API strips only to decide presence and `schemas.py` passes the value
 * through raw. The review history behind each of these notes lives in the
 * PRD's Review Iterations table, not here.
 *
 * What this still canNOT distinguish: own `hubspot_id` with a NULL
 * `hubspot_email` — the state that actually fails silently, since the
 * sender resolver returns `None` and falls back to whatever the frontend
 * supplied. `UserMe` does not carry `hubspot_email`, so the own-id copy
 * below deliberately says what is KNOWN (HubSpot is not Jared's) rather
 * than what is not (that it acts as the engineer). Iter 1 reported this
 * state and iter 2 correctly pointed out that the first fix addressed an
 * adjacent one instead. A contract field would settle it; that is a
 * recorded follow-up, not this PR.
 *
 * Tokenized: composition only — the visual treatment lives in the shared
 * `<ImpersonationBanner>` primitive from `@nu-assist/ui`.
 */
import { useMe } from "@nu-assist/shared-fe";
import { ImpersonationBanner } from "@nu-assist/ui";

const JARED_THRIVE_ID = "2";
const JARED_EMAIL = "jared@nusearch.com";
const JARED_HUBSPOT_ID = "78133807";

export function ImpersonationBannerSlot() {
  const me = useMe();
  if (me.status !== "authenticated") return null;

  // `thrive_id` and `hubspot_id` reach the browser RAW: `_provisioning_flags`
  // strips them only for the presence test and `_build_user_me` copies the
  // original value, with no validator in `schemas.py`. `email` does NOT —
  // `_normalize_email` strips it on both the login and the /auth/me path, so
  // a padded address cannot get here. It is trimmed below for symmetry, as
  // defence in depth, not because a live state requires it.
  //
  // The `thrive_id` trim is the one that matters. Untrimmed, a padded " 2 "
  // mismatches JARED_THRIVE_ID, this returns null, and the banner vanishes
  // entirely while the engineer is impersonating Jared — with nothing on
  // screen saying so. That value does get past the gate, because
  // `_provisioning_flags` strips before the presence test.
  const thriveId = me.data.thrive_id?.trim();
  const email = me.data.email?.trim();
  if (thriveId !== JARED_THRIVE_ID) return null;
  if (email === JARED_EMAIL) return null;

  const hubspotId = me.data.hubspot_id?.trim();

  const hubspotNote = !hubspotId
    ? "Downstream Thrive calls hit Jared's real records. No HubSpot user is linked to this account."
    : hubspotId === JARED_HUBSPOT_ID
      ? "Downstream Thrive + HubSpot calls hit Jared's real records."
      : "Downstream Thrive calls hit Jared's real records; HubSpot calls do not use Jared's user.";

  return (
    <ImpersonationBanner
      signedInAs={email}
      actingAs={`Jared Cohen (Thrive id=${JARED_THRIVE_ID})`}
    >
      {hubspotNote}
    </ImpersonationBanner>
  );
}
